import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import useStore from '../../store';
import { leavesApi, attendanceApi, holidaysApi, leavePoliciesApi } from '../../services/api';
import './Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const LEAVE_COLORS = {
  earned:   { dot: '#7C3AED', bg: '#EDE9FE' },
  sick:     { dot: '#0EA5E9', bg: '#E0F2FE' },
  casual:   { dot: '#10B981', bg: '#D1FAE5' },
  maternity:{ dot: '#F59E0B', bg: '#FEF3C7' },
  paternity:{ dot: '#6366F1', bg: '#EEF2FF' },
  unpaid:   { dot: '#9CA3AF', bg: '#F3F4F6' },
  Special:  { dot: '#F97316', bg: '#FFEDD5' },
};

export default function Dashboard() {
  const {
    user,
    leaveBalances,
    setLeaveBalances,
    setTakenSickLeave,
    takenSickLeave,
    setTakenSpecialLeave,
    takenSpecialLeave,
  } = useStore();
  const navigate = useNavigate();
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [attendanceChartData, setAttendanceChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState(null);

  const role = user?.role;
  const year = new Date().getFullYear();
  const month = new Date().getMonth() + 1;

  useEffect(() => {
    const load = async () => {
      try {
        const [balRes, sumRes, holRes, leaveRes] = await Promise.all([
          leavesApi.balance({ year, userId: user?.id }),
          attendanceApi.summary({ year, month, userId: user?.id }),
          holidaysApi.list({ year }),
          role !== 'employee'
            ? leavesApi.list({ status: 'pending', limit: 5 })
            : leavesApi.list({ limit: 5 }),
        ]);
        const balPayload = balRes.data;
        let finalBalances = balPayload.data;
        let policyData = null;
        if (user?.leave_policy_id) {
          try {
            const polRes = await leavePoliciesApi.get(user.leave_policy_id);
            policyData = polRes.data.data || null;
            if (policyData?.balances?.length) {
              finalBalances = policyData.balances.map((pb) => {
                const userBal = balPayload.data.find((b) => String(b.leave_type || '').toLowerCase() === String(pb.leave_type || '').toLowerCase());
                const totalAllocated = parseFloat(pb.allocated_days || 0);
                const rollover = parseFloat(userBal?.rollover || 0) || 0;
                const taken = parseFloat(userBal?.taken || 0) || 0;
                return {
                  id: userBal?.id || `policy-${pb.leave_type}`,
                  leave_type: pb.leave_type,
                  total_allocated: totalAllocated,
                  rollover,
                  taken,
                };
              });
            }
          } catch (e) {
            // Ignore policy fetch errors and fall back to server balances
          }
        }

        setLeaveBalances(finalBalances);
        setPolicy(policyData);
        setTakenSickLeave(balPayload.takenSickLeave);
        setTakenSpecialLeave(balPayload.takenSpecialLeave);
        setAttendanceSummary(sumRes.data.data);
        const today = new Date().toISOString().split('T')[0];
        const sourceHolidays = policyData?.holidays?.length ? policyData.holidays : holRes.data.data;
        const upcoming = sourceHolidays
          .filter((h) => h.date >= today)
          .slice(0, 5);
        setUpcomingHolidays(upcoming);
        setPendingLeaves(leaveRes.data.data);

        // Build attendance trend for last 7 working days
        const attRes = await attendanceApi.list({ limit: 14, userId: user?.id });
        const records = attRes.data.data.slice(0, 7).reverse();
        setAttendanceChartData({
          labels: records.map((r) =>
            new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
          ),
          datasets: [{
            label: 'Status (1=Working, 0.5=Half, 0=Off)',
            data: records.map((r) =>
              r.status === 'working' ? 1 : r.status === 'half_day' ? 0.5 : 0
            ),
            borderColor: '#7C3AED',
            backgroundColor: 'rgba(124,58,237,.08)',
            fill: true,
            tension: 0,
            pointBackgroundColor: '#7C3AED',
            pointRadius: 5,
          }],
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [month, role, setLeaveBalances, setTakenSickLeave, setTakenSpecialLeave, year]);

  const statusBadge = (status) => {
    const map = {
      pending: 'badge-amber', approved: 'badge-emerald',
      rejected: 'badge-rose', cancelled: 'badge-gray',
    };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="dashboard">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 120, borderRadius: 16, marginBottom: 16 }} />
        ))}
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Welcome banner */}
      <div className="dashboard-welcome animate-in">
        <div className="dashboard-welcome-text">
          <h1>Welcome back 👋</h1>
          <h2>{user?.first_name} {user?.last_name}</h2>
          <p>
            {user?.designation} · {user?.department?.name} ·{' '}
            <span className={`badge ${user?.employee_type === 'contractor' ? 'badge-amber' : 'badge-violet'}`}>
              {user?.employee_type === 'contractor' ? 'Contractor' : 'Full Time'}
            </span>
          </p>
          <div className="dashboard-welcome-actions">
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/attendance')}>
              Log Today's Attendance
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/leaves')}>
              Apply for Leave
            </button>
          </div>
        </div>
        <div className="dashboard-welcome-illustration" aria-hidden>
          <div className="welcome-orb welcome-orb-1" />
          <div className="welcome-orb welcome-orb-2" />
          <span className="welcome-emoji">🎯</span>
        </div>
      </div>

      {/* Leave balance cards */}
      <div className="dashboard-stats animate-in stagger-1">
        {leaveBalances.map((bal, i) => {
          const colors = LEAVE_COLORS[bal.leave_type] || LEAVE_COLORS.unpaid;
          const pct = bal.total_allocated > 0
            ? Math.min(100, (bal.taken / bal.total_allocated) * 100)
            : 0;
          return (
            <div key={bal.id || i} className="leave-bal-card">
              <div className="leave-bal-header">
                <span className="leave-bal-type" style={{ color: colors.dot }}>
                  {bal.leave_type.charAt(0).toUpperCase() + bal.leave_type.slice(1)} Leaves Availed
                </span>
                <span className="leave-bal-number">{bal.taken ?? 0}</span>
              </div>
              <div className="leave-bal-bar">
                <div
                  className="leave-bal-bar-fill"
                  style={{ width: `${pct}%`, background: colors.dot }}
                />
              </div>
              <div className="leave-bal-footer">
                {bal.rollover > 0 && <span>Rollover: <strong>{bal.rollover}</strong></span>}
                <span>Total: <strong>{bal.total_allocated}</strong></span>
              </div>
            </div>
          );
        })}
        <div className="leave-bal-card">
          <div className="leave-bal-header">
            <span className="leave-bal-type" style={{ color: LEAVE_COLORS.sick.dot }}>
              Sick Leaves Availed
            </span>
            <span className="leave-bal-number">{takenSickLeave ?? 0}</span>
          </div>
          <div className="leave-bal-bar">
            <div
              className="leave-bal-bar-fill"
              style={{ width: takenSickLeave ? '100%' : '0%', background: LEAVE_COLORS.sick.dot }}
            />
          </div>
        </div>
        <div className="leave-bal-card">
          <div className="leave-bal-header">
            <span className="leave-bal-type" style={{ color: LEAVE_COLORS.Special.dot }}>
              Special Leaves Availed
            </span>
            <span className="leave-bal-number">{takenSpecialLeave ?? 0}</span>
          </div>
          <div className="leave-bal-bar">
            <div
              className="leave-bal-bar-fill"
              style={{ width: takenSpecialLeave ? '100%' : '0%', background: LEAVE_COLORS.Special.dot }}
            />
          </div>
        </div>
      </div>

      {/* Middle row */}
      <div className="dashboard-mid animate-in stagger-2">
        {/* Attendance chart */}
        <div className="card dashboard-chart-card">
          <div className="card-header">
            <h3>Attendance Trend</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/attendance')}>View all →</button>
          </div>
          <div className="card-body">
            {attendanceChartData ? (
              <Line
                data={attendanceChartData}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false }, tooltip: { mode: 'index' } },
                  scales: {
                    y: { min: 0, max: 1.25, ticks: { stepSize: 0.5 }, grid: { color: '#F3F4F6' } },
                    x: { grid: { display: false } },
                  },
                }}
              />
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📊</div>
                <div className="empty-state-title">No attendance data yet</div>
              </div>
            )}
          </div>
          {attendanceSummary && (
            <div className="attendance-summary-row">
              <div className="att-chip att-working">
                <strong>{attendanceSummary.working}</strong> Working
              </div>
              <div className="att-chip att-half">
                <strong>{attendanceSummary.half_day}</strong> Half Days
              </div>
              <div className="att-chip att-off">
                <strong>{attendanceSummary.not_working}</strong> Off
              </div>
            </div>
          )}
        </div>

        {/* Upcoming holidays */}
        <div className="card dashboard-holidays-card">
          <div className="card-header">
            <h3>Upcoming Holidays</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/holidays')}>All →</button>
          </div>
          <div className="card-body">
            {upcomingHolidays.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🎉</div>
                <div className="empty-state-title">No upcoming holidays</div>
              </div>
            ) : (
              <ul className="holiday-list">
                {upcomingHolidays.map((h) => (
                  <li key={h.id} className="holiday-item">
                    <div className="holiday-date-badge">
                      <span className="holiday-day">
                        {new Date(h.date).toLocaleDateString('en-IN', { day: '2-digit' })}
                      </span>
                      <span className="holiday-month">
                        {new Date(h.date).toLocaleDateString('en-IN', { month: 'short' })}
                      </span>
                    </div>
                    <div className="holiday-info">
                      <span className="holiday-name">{h.name}</span>
                      <span className={`badge ${h.type === 'common' ? 'badge-violet' : 'badge-sky'}`}>
                        {h.type === 'common' ? 'National' : `Regional · ${h.region?.name || ''}`}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Leave requests table */}
      <div className="card animate-in stagger-3">
        <div className="card-header">
          <h3>
            {role !== 'employee' ? 'Pending Leave Requests' : 'My Recent Leave Requests'}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(role !== 'employee' ? '/team/leaves' : '/leaves')}>
            View all →
          </button>
        </div>
        {pendingLeaves.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px 20px' }}>
            <div className="empty-state-icon">✅</div>
            <div className="empty-state-title">No leave requests to show</div>
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  {role !== 'employee' && <th>Employee</th>}
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingLeaves.map((lr) => (
                  <tr key={lr.id}>
                    {role !== 'employee' && (
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar avatar-sm">
                            {lr.requester?.first_name?.[0]}{lr.requester?.last_name?.[0]}
                          </div>
                          <span>{lr.requester?.first_name} {lr.requester?.last_name}</span>
                        </div>
                      </td>
                    )}
                    <td>
                      <span className="leave-type-tag">{lr.leave_type}</span>
                    </td>
                    <td>{new Date(lr.start_date).toLocaleDateString('en-IN')}</td>
                    <td>{new Date(lr.end_date).toLocaleDateString('en-IN')}</td>
                    <td><strong>{lr.days_requested}</strong></td>
                    <td>{statusBadge(lr.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
